import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Select } from "./ui/Select";
import { Card, CardBody, CardHeader } from "./ui/Card";
import { Chip } from "./ui/Chip";
import { useAuth } from "../hooks/useAuth";
import { fetchAllSaints } from "../services/saints";
import {
  CATEGORY_GROUPS,
  CATEGORY_INFO,
  STAGES,
  STAGE_INFO,
} from "../lib/saints/constants";
import {
  COUNTRY_SELECT_GROUPS,
  normalizeCountryLower,
  countryOption,
} from "../lib/saints/countries";
import CountryFlag from "./CountryFlag";
import { feastSummary, parseFeastKey } from "../lib/saints/feasts";
import { lower, toTokens } from "../lib/saints/search";
import {
  FaShareAlt,
  FaPencilAlt,
  FaSyncAlt,
  FaHeart,
  FaEye,
  FaSearch,
} from "react-icons/fa";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function daysInMonthLoose(month) {
  if (!month) return 31;
  if ([4, 6, 9, 11].includes(month)) return 30;
  if (month === 2) return 29;
  return 31;
}

function monthOptions() {
  return Array.from({ length: 12 }, (_, i) => i + 1);
}

function dayOptions(month) {
  const m = Number(month);
  const max = daysInMonthLoose(m);
  return Array.from({ length: max }, (_, i) => i + 1);
}

function patronagePreview(s, n = 3) {
  const p = Array.isArray(s?.patronage) ? s.patronage : [];
  if (!p.length) return "";
  const shown = p.slice(0, n).join(", ");
  const more = p.length > n ? ` + ${p.length - n}` : "";
  return `Patron: ${shown}${more}`;
}

function getFeastPairs(s) {
  const keys = Array.isArray(s?.feastKeys) ? s.feastKeys : null;
  if (keys && keys.length) return keys.map(parseFeastKey).filter(Boolean);

  if (s?.feastKey) {
    const p = parseFeastKey(s.feastKey);
    return p ? [p] : [];
  }
  if (s?.feastMonth && s?.feastDay) {
    const mm = String(Number(s.feastMonth)).padStart(2, "0");
    const dd = String(Number(s.feastDay)).padStart(2, "0");
    return [
      {
        month: Number(s.feastMonth),
        day: Number(s.feastDay),
        key: `${mm}-${dd}`,
      },
    ];
  }
  return [];
}

function extractMonthDay(text) {
  if (!text) return null;
  const raw = String(text).trim();
  if (!raw) return null;

  if (/^\s*(c\.|ca\.|circa)\b/i.test(raw)) return null;

  const s = raw
    .replace(/\(.*?\)/g, " ")
    .replace(/,/g, " ")
    .replace(/\./g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    if (m >= 1 && m <= 12 && d >= 1 && d <= daysInMonthLoose(m)) {
      return { month: m, day: d };
    }
  }

  const md = s.match(/\b(\d{1,2})-(\d{1,2})\b/);
  if (md) {
    const m = Number(md[1]);
    const d = Number(md[2]);
    if (m >= 1 && m <= 12 && d >= 1 && d <= daysInMonthLoose(m)) {
      return { month: m, day: d };
    }
  }

  const monthRe =
    /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b/i;

  const mMatch = s.match(monthRe);
  if (mMatch) {
    const mStr = mMatch[1].toLowerCase();
    const map = {
      jan: 1,
      january: 1,
      feb: 2,
      february: 2,
      mar: 3,
      march: 3,
      apr: 4,
      april: 4,
      may: 5,
      jun: 6,
      june: 6,
      jul: 7,
      july: 7,
      aug: 8,
      august: 8,
      sep: 9,
      sept: 9,
      september: 9,
      oct: 10,
      october: 10,
      nov: 11,
      november: 11,
      dec: 12,
      december: 12,
    };

    const month = map[mStr];
    if (!month) return null;

    {
      const after = s.slice((mMatch.index ?? 0) + mMatch[0].length);
      const dMatch = after.match(/\b(\d{1,2})\b/);
      if (dMatch) {
        const day = Number(dMatch[1]);
        if (day >= 1 && day <= daysInMonthLoose(month)) return { month, day };
      }
    }

    {
      const before = s.slice(0, mMatch.index ?? 0);
      const dMatch = before.match(/(\d{1,2})\s*$/);
      if (dMatch) {
        const day = Number(dMatch[1]);
        if (day >= 1 && day <= daysInMonthLoose(month)) return { month, day };
      }
    }
  }

  return null;
}

function matchMonthDay(sourceText, month, day) {
  const m = Number(month);
  const d = day ? Number(day) : null;
  if (!m) return true;

  const md = extractMonthDay(sourceText);
  if (!md) return false;
  if (md.month !== m) return false;
  if (d && md.day !== d) return false;
  return true;
}

function heroFromSaint(s) {
  const g = Array.isArray(s?.gallery) ? s.gallery : [];
  return g.length ? g[0] : "";
}

const PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

function pageSeries(totalPages, currentPage) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const keep = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
  const pages = Array.from(keep)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const out = [];
  for (let i = 0; i < pages.length; i++) {
    const p = pages[i];
    const prev = pages[i - 1];
    if (i > 0 && p - prev > 1) out.push("…");
    out.push(p);
  }
  return out;
}

function getViewsCount(s) {
  const n = Number(s?.views);
  return Number.isFinite(n) ? n : 0;
}

function getFavoritesCount(s) {
  return s.favorites?.length || 0;
}

function InfoTooltip({ title, body }) {
  if (!body) return null;

  return (
    <div className="pointer-events-none absolute left-1/2 top-0 z-20 hidden w-72 -translate-x-1/2 -translate-y-[calc(100%+0.5rem)] rounded-2xl border border-dulia-line bg-dulia-panel p-3 text-xs shadow-card group-hover:block group-focus-within:block dark:border-dulia-lineStrong/30 dark:bg-dulia-night">
      <div className="font-semibold text-dulia-ink dark:text-dulia-parchment">
        {title}
      </div>
      <div className="mt-1 leading-snug text-dulia-navy/80 dark:text-dulia-parchment/75">
        {body}
      </div>
    </div>
  );
}

function FilterTag({ active, onClick, label, info }) {
  return (
    <div className="group relative">
      <InfoTooltip title={label} body={info} />
      <Chip active={active} onClick={onClick} aria-pressed={active} title={info || undefined}>
        {label}
      </Chip>
    </div>
  );
}

function SelectionPill({ children, onRemove, ariaLabel }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-dulia-line bg-dulia-panel px-3 py-1.5 text-sm text-dulia-ink dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment">
      {children}
      <button
        type="button"
        className="rounded-md px-2 py-1 text-xs text-dulia-navy/70 transition hover:bg-dulia-parchment dark:text-dulia-parchment/75 dark:hover:bg-dulia-nightSoft"
        onClick={onRemove}
        aria-label={ariaLabel}
      >
        ✕
      </button>
    </span>
  );
}

function MetricPill({ icon, value, tone = "default" }) {
  const toneClass =
    tone === "gold"
      ? "border-dulia-lineStrong bg-dulia-parchment text-dulia-navy dark:border-dulia-gold/35 dark:bg-dulia-night dark:text-dulia-goldSoft"
      : "border-dulia-line bg-dulia-panel text-dulia-navy dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${toneClass}`}
    >
      {icon}
      {value}
    </span>
  );
}

function PaginationButton({ active = false, children, ...props }) {
  return (
    <button
      type="button"
      className={[
        "h-9 min-w-9 rounded-xl border px-3 text-sm font-medium transition",
        active
          ? "border-dulia-navy bg-dulia-navy text-white shadow-soft dark:border-dulia-gold dark:bg-dulia-gold dark:text-dulia-night"
          : "border-dulia-line bg-dulia-panel text-dulia-navy hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment dark:hover:bg-dulia-nightSoft",
      ].join(" ")}
      {...props}
    >
      {children}
    </button>
  );
}

function ToolbarLabel({ children }) {
  return (
    <span className="text-sm font-medium text-dulia-navy/75 dark:text-dulia-parchment/75">
      {children}
    </span>
  );
}

export default function SaintsSearchList({
  title = "Search",
  showAdminActions = false,
  showNewButton = false,
}) {
  const [loading, setLoading] = useState(true);
  const [saints, setSaints] = useState([]);
  const { user, admin } = useAuth();
  const isAdmin = admin;
  const canRequest = !!user && !isAdmin;
  const [q, setQ] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const navigate = useNavigate();

  const [stageSel, setStageSel] = useState(() => new Set([]));
  const [categorySel, setCategorySel] = useState(() => new Set());
  const [countryPick, setCountryPick] = useState("");
  const [countriesSel, setCountriesSel] = useState([]);

  const [feastMonth, setFeastMonth] = useState("");
  const [feastDay, setFeastDay] = useState("");

  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");

  const [deathMonth, setDeathMonth] = useState("");
  const [deathDay, setDeathDay] = useState("");

  const [canonMonth, setCanonMonth] = useState("");
  const [canonDay, setCanonDay] = useState("");

  const [patronPick, setPatronPick] = useState("");
  const [patronSel, setPatronSel] = useState([]);

  const [perPage, setPerPage] = useState(100);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("favorites_desc");

  const resultsRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const all = await fetchAllSaints();
      setSaints(all);
      setLoading(false);
    })();
  }, []);

  function onEnterScroll(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function toggleCategory(c) {
    setCategorySel((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }

  function toggleStage(s) {
    setStageSel((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  function addCountry(value) {
    const v = String(value || "").toLowerCase().trim();
    if (!v) return;
    setCountriesSel((prev) => (prev.includes(v) ? prev : [...prev, v]));
  }

  function removeCountry(v) {
    setCountriesSel((prev) => prev.filter((x) => x !== v));
  }

  function addPatronage(raw) {
    const parts = String(raw || "")
      .split(",")
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);

    if (!parts.length) return;

    setPatronSel((prev) => {
      const set = new Set(prev);
      for (const p of parts) set.add(p);
      return Array.from(set);
    });
  }

  function removePatronage(v) {
    setPatronSel((prev) => prev.filter((x) => x !== v));
  }

  function clearAll() {
    setQ("");
    setStageSel(new Set([]));
    setCategorySel(new Set());
    setCountryPick("");
    setCountriesSel([]);

    setFeastMonth("");
    setFeastDay("");
    setBirthMonth("");
    setBirthDay("");
    setDeathMonth("");
    setDeathDay("");
    setCanonMonth("");
    setCanonDay("");

    setPatronPick("");
    setPatronSel([]);

    setSortBy("favorites_desc");
    setPage(1);
  }

  const filtered = useMemo(() => {
    const tokens = toTokens(q);
    const catArr = Array.from(categorySel);
    const stageArr = Array.from(stageSel);

    const fM = feastMonth ? Number(feastMonth) : null;
    const fD = feastDay ? Number(feastDay) : null;

    const bM = birthMonth ? Number(birthMonth) : null;
    const bD = birthDay ? Number(birthDay) : null;

    const dM = deathMonth ? Number(deathMonth) : null;
    const dD = deathDay ? Number(deathDay) : null;

    const cM = canonMonth ? Number(canonMonth) : null;
    const cD = canonDay ? Number(canonDay) : null;

    return saints.filter((s) => {
      const nameLower = lower(s.nameLower || s.name);

      if (tokens.length) {
        const subs = Array.isArray(s.nameSubstrings) ? s.nameSubstrings : [];
        const ok =
          tokens.every((t) => nameLower.includes(t)) ||
          tokens.some((t) => subs.includes(t));
        if (!ok) return false;
      }

      if (stageArr.length) {
        if (!stageArr.includes(s.stage)) return false;
      }

      if (catArr.length) {
        const saintCats = Array.isArray(s.categories) ? s.categories : [];
        const has = (c) => saintCats.includes(c) || (c === "Pope" && !!s.isPope);
        if (!catArr.some(has)) return false;
      }

      if (countriesSel.length) {
        const saintCountry = normalizeCountryLower(s);
        if (!countriesSel.includes(saintCountry)) return false;
      }

      if (fM) {
        const pairs = getFeastPairs(s);
        const ok = pairs.some((p) => p.month === fM && (!fD || p.day === fD));
        if (!ok) return false;
      }

      if (bM) {
        if (!matchMonthDay(s?.bornText, bM, bD)) return false;
      }

      if (dM) {
        if (!matchMonthDay(s?.diedText, dM, dD)) return false;
      }

      if (cM) {
        if (!matchMonthDay(s?.canonizedText, cM, cD)) return false;
      }

      if (patronSel.length) {
        const pats = Array.isArray(s?.patronageLower)
          ? s.patronageLower
          : (Array.isArray(s?.patronage) ? s.patronage : []).map((p) =>
              String(p || "").toLowerCase().trim()
            );

        const ok = patronSel.some((needle) =>
          pats.some((p) => String(p || "").includes(needle))
        );
        if (!ok) return false;
      }

      return true;
    });
  }, [
    saints,
    q,
    stageSel,
    categorySel,
    countriesSel,
    feastMonth,
    feastDay,
    birthMonth,
    birthDay,
    deathMonth,
    deathDay,
    canonMonth,
    canonDay,
    patronSel,
  ]);

  const sorted = useMemo(() => {
    const withIdx = filtered.map((s, idx) => ({ s, idx }));

    const nameKey = (s) => lower(s?.nameLower || s?.name || "");
    const idKey = (s) => String(s?.id || "");

    const comparators = {
      name_asc: (A, B) => nameKey(A).localeCompare(nameKey(B)),
      name_desc: (A, B) => nameKey(B).localeCompare(nameKey(A)),
      views_desc: (A, B) => getViewsCount(B) - getViewsCount(A),
      views_asc: (A, B) => getViewsCount(A) - getViewsCount(B),
      favorites_desc: (A, B) => getFavoritesCount(B) - getFavoritesCount(A),
      favorites_asc: (A, B) => getFavoritesCount(A) - getFavoritesCount(B),
    };

    const sortChain =
      sortBy === "favorites_desc"
        ? ["favorites_desc", "views_desc", "name_asc"]
        : sortBy === "views_desc"
          ? ["views_desc", "favorites_desc", "name_asc"]
          : [sortBy, "views_desc", "favorites_desc", "name_asc"];

    const cmp = (a, b) => {
      const A = a.s;
      const B = b.s;

      for (const key of sortChain) {
        const result = comparators[key]?.(A, B) ?? 0;
        if (result) return result;
      }

      const i = idKey(A).localeCompare(idKey(B));
      if (i) return i;

      return a.idx - b.idx;
    };

    withIdx.sort(cmp);
    return withIdx.map((x) => x.s);
  }, [filtered, sortBy]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  useEffect(() => {
    setPage(1);
  }, [
    q,
    stageSel,
    categorySel,
    countriesSel,
    feastMonth,
    feastDay,
    birthMonth,
    birthDay,
    deathMonth,
    deathDay,
    canonMonth,
    canonDay,
    patronSel,
    sortBy,
  ]);

  function goToPage(next) {
    const p = Math.min(Math.max(1, next), totalPages);
    setPage(p);
  }

  const startIndex = total === 0 ? 0 : (page - 1) * perPage;
  const endIndex = Math.min(startIndex + perPage, total);

  const paged = useMemo(() => {
    return sorted.slice(startIndex, endIndex);
  }, [sorted, startIndex, endIndex]);

  function shareSaint(s) {
    const url = `${window.location.origin}/profile/${s.id}`;
    const shareTitle = s?.name || "Dulia";

    if (navigator.share) {
      navigator.share({ title: shareTitle, text: shareTitle, url }).catch(() => {});
      return;
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).catch(() => {});
      return;
    }

    try {
      const el = document.createElement("textarea");
      el.value = url;
      el.style.position = "fixed";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    } catch {}
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[30px] bg-dulia-hero p-6 text-white shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dulia-goldSoft">
              <FaSearch aria-hidden="true" />
              Search Profiles
            </div>
            <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
              {title}
            </h1>
            <p className="max-w-2xl text-sm leading-6 text-white/80 md:text-base">
              Use Dulia's powerful search tool to find saints by name, stage, feast day, patronage, country, and more. You can combine multiple filters to narrow down your search and discover saints that match specific criteria.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {showNewButton && isAdmin ? (
              <Button type="button" variant="primary" onClick={() => navigate("/admin/new")}>
                + New
              </Button>
            ) : null}

            {showNewButton && canRequest ? (
              <Button type="button" variant="outline" onClick={() => navigate("/request/")}>
                + Request
              </Button>
            ) : null}

            <Button type="button" variant="outline" onClick={clearAll}>
              <span className="inline-flex items-center gap-2">
                <FaSyncAlt aria-hidden="true" />
                <span>Reset Filters</span>
              </span>
            </Button>
          </div>
        </div>
      </section>

      <Card className="overflow-hidden rounded-[30px]">
        <CardHeader className="px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-dulia-ink dark:text-dulia-parchment">
                Refine Results
              </h2>
              <p className="mt-1 text-sm text-dulia-navy/75 dark:text-dulia-parchment/70">
                Use as many or as few filters as you like.
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              className="md:hidden"
              onClick={() => setFiltersOpen((open) => !open)}
              aria-expanded={filtersOpen}
              aria-controls="search-filters-panel"
            >
              {filtersOpen ? "Hide Filters" : "Show Filters"}
            </Button>
          </div>
        </CardHeader>

        <CardBody className="space-y-6">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
              Name Search
            </label>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onEnterScroll}
              placeholder="Search by name..."
            />
          </div>

          <div
            id="search-filters-panel"
            className={`${filtersOpen ? "block" : "hidden"} space-y-6 md:block`}
          >
            <div className="space-y-2">
              <div className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
                Stage
              </div>
              <div className="flex flex-wrap gap-2">
                {STAGES.map((s) => (
                  <FilterTag
                    key={s}
                    label={s}
                    active={stageSel.has(s)}
                    info={STAGE_INFO[s]}
                    onClick={() => toggleStage(s)}
                  />
                ))}
              </div>
              <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
                Selecting multiple stages matches those in{" "}
                <span className="font-semibold">any</span> of them.
              </div>
            </div>

            <div className="space-y-3">
            <div className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
              Tags
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {CATEGORY_GROUPS.map((group) => (
                <div
                  key={group.label}
                  className="rounded-2xl border border-dulia-line/80 bg-dulia-parchment/55 p-4 dark:border-dulia-lineStrong/20 dark:bg-dulia-night"
                >
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                    {group.label}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {group.options.map((c) => (
                      <FilterTag
                        key={c}
                        label={c}
                        active={categorySel.has(c)}
                        info={CATEGORY_INFO[c]}
                        onClick={() => toggleCategory(c)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
              Selecting multiple tags matches those in{" "}
              <span className="font-semibold">any</span> of them.
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <div className="space-y-3">
              <div className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
                Countries
              </div>

              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-[240px] flex-1">
                  <Select
                    value={countryPick}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCountryPick("");
                      addCountry(v);
                    }}
                    onKeyDown={onEnterScroll}
                  >
                    <option value="">Add a country…</option>
                    {COUNTRY_SELECT_GROUPS.map((g) => (
                      <optgroup key={g.label} label={g.label}>
                        {g.options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                </div>

                {countriesSel.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {countriesSel.map((v) => (
                      <SelectionPill
                        key={v}
                        onRemove={() => removeCountry(v)}
                        ariaLabel={`Remove ${countryOption(v)?.name || v}`}
                      >
                        <CountryFlag
                          value={v}
                          showName
                          imgClassName="h-4 w-6 rounded-sm border border-black/10 object-cover"
                          nameClassName="text-sm dark:text-dulia-parchment"
                        />
                      </SelectionPill>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
                Selecting multiple countries matches those in{" "}
                <span className="font-semibold">any</span> of them.
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
                Patronage
              </div>

              <div className="flex flex-wrap items-start gap-2">
                <div className="min-w-[240px] flex-1">
                  <Input
                    value={patronPick}
                    onChange={(e) => setPatronPick(e.target.value)}
                    placeholder='Add patronage… (e.g. "students", "families")'
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addPatronage(patronPick);
                        setPatronPick("");
                      }
                    }}
                  />
                </div>

                <Button
                  type="button"
                  onClick={() => {
                    addPatronage(patronPick);
                    setPatronPick("");
                  }}
                >
                  Add
                </Button>

                {patronSel.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {patronSel.map((v) => (
                      <SelectionPill
                        key={v}
                        onRemove={() => removePatronage(v)}
                        ariaLabel={`Remove ${v}`}
                      >
                        {v}
                      </SelectionPill>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
                Selecting multiple patronage strings matches saints with{" "}
                <span className="font-semibold">any</span> of them.
                <br/>
                Try using synonyms or related terms to find more profiles. For example, "programmers" would return St. Carlo Acutis, but "coders" would not.
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
              Date Filters
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-dulia-line/80 bg-dulia-parchment/55 p-4 dark:border-dulia-lineStrong/20 dark:bg-dulia-night">
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                  Birth Day
                </div>
                <div className="flex gap-2">
                  <Select
                    value={birthMonth}
                    onChange={(e) => {
                      setBirthMonth(e.target.value);
                      setBirthDay("");
                    }}
                    onKeyDown={onEnterScroll}
                  >
                    <option value="">Month</option>
                    {monthOptions().map((m) => (
                      <option key={m} value={m}>
                        {MONTH_NAMES[m - 1]}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={birthDay}
                    onChange={(e) => setBirthDay(e.target.value)}
                    disabled={!birthMonth}
                    onKeyDown={onEnterScroll}
                  >
                    <option value="">{birthMonth ? "Any day" : "Day"}</option>
                    {birthMonth &&
                      dayOptions(birthMonth).map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                  </Select>
                </div>
              </div>

              <div className="rounded-2xl border border-dulia-line/80 bg-dulia-parchment/55 p-4 dark:border-dulia-lineStrong/20 dark:bg-dulia-night">
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                  Death Day
                </div>
                <div className="flex gap-2">
                  <Select
                    value={deathMonth}
                    onChange={(e) => {
                      setDeathMonth(e.target.value);
                      setDeathDay("");
                    }}
                    onKeyDown={onEnterScroll}
                  >
                    <option value="">Month</option>
                    {monthOptions().map((m) => (
                      <option key={m} value={m}>
                        {MONTH_NAMES[m - 1]}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={deathDay}
                    onChange={(e) => setDeathDay(e.target.value)}
                    disabled={!deathMonth}
                    onKeyDown={onEnterScroll}
                  >
                    <option value="">{deathMonth ? "Any day" : "Day"}</option>
                    {deathMonth &&
                      dayOptions(deathMonth).map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                  </Select>
                </div>
              </div>

              <div className="rounded-2xl border border-dulia-line/80 bg-dulia-parchment/55 p-4 dark:border-dulia-lineStrong/20 dark:bg-dulia-night">
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                  Canonization Day
                </div>
                <div className="flex gap-2">
                  <Select
                    value={canonMonth}
                    onChange={(e) => {
                      setCanonMonth(e.target.value);
                      setCanonDay("");
                    }}
                    onKeyDown={onEnterScroll}
                  >
                    <option value="">Month</option>
                    {monthOptions().map((m) => (
                      <option key={m} value={m}>
                        {MONTH_NAMES[m - 1]}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={canonDay}
                    onChange={(e) => setCanonDay(e.target.value)}
                    disabled={!canonMonth}
                    onKeyDown={onEnterScroll}
                  >
                    <option value="">{canonMonth ? "Any day" : "Day"}</option>
                    {canonMonth &&
                      dayOptions(canonMonth).map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                  </Select>
                </div>
              </div>
              <div className="rounded-2xl border border-dulia-line/80 bg-dulia-parchment/55 p-4 dark:border-dulia-lineStrong/20 dark:bg-dulia-night">
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                  Feast Day
                </div>
                <div className="flex gap-2">
                  <Select
                    value={feastMonth}
                    onChange={(e) => {
                      setFeastMonth(e.target.value);
                      setFeastDay("");
                    }}
                    onKeyDown={onEnterScroll}
                  >
                    <option value="">Month</option>
                    {monthOptions().map((m) => (
                      <option key={m} value={m}>
                        {MONTH_NAMES[m - 1]}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={feastDay}
                    onChange={(e) => setFeastDay(e.target.value)}
                    disabled={!feastMonth}
                    onKeyDown={onEnterScroll}
                  >
                    <option value="">{feastMonth ? "Any day" : "Day"}</option>
                    {feastMonth &&
                      dayOptions(feastMonth).map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                  </Select>
                </div>
              </div>
            </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <div ref={resultsRef} className="space-y-4">
        {!loading && sorted.length > 0 ? (
          <Card className="rounded-[24px]">
            <CardBody className="flex flex-wrap items-center justify-between gap-4">
              <div className="text-sm text-dulia-navy/75 dark:text-dulia-parchment/75">
                Showing <span className="font-semibold">{startIndex + 1}</span>–
                <span className="font-semibold">{endIndex}</span> of{" "}
                <span className="font-semibold">{total}</span>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <ToolbarLabel>Sort by</ToolbarLabel>
                  <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="name_asc">Name (A → Z)</option>
                    <option value="name_desc">Name (Z → A)</option>
                    <option value="favorites_desc">♥ Favorites (high → low)</option>
                    <option value="favorites_asc">♥ Favorites (low → high)</option>
                    <option value="views_desc">👁 Views (high → low)</option>
                    <option value="views_asc">👁 Views (low → high)</option>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <ToolbarLabel>Per page</ToolbarLabel>
                  <Select
                    value={String(perPage)}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      goToPage(1);
                    }}
                  >
                    {PER_PAGE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1}
                    aria-label="Previous page"
                    title="Previous page"
                  >
                    <span aria-hidden="true">←</span>
                  </Button>

                  {pageSeries(totalPages, page).map((p, idx) =>
                    p === "…" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-2 text-sm text-dulia-navy/60 dark:text-dulia-parchment/60"
                      >
                        …
                      </span>
                    ) : (
                      <PaginationButton
                        key={p}
                        active={p === page}
                        onClick={() => goToPage(p)}
                        aria-current={p === page ? "page" : undefined}
                      >
                        {p}
                      </PaginationButton>
                    )
                  )}

                  <Button
                    type="button"
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= totalPages}
                    aria-label="Next page"
                    title="Next page"
                  >
                    <span aria-hidden="true">→</span>
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        ) : null}

        {loading ? (
          <Card className="rounded-[24px]">
            <CardBody className="text-dulia-navy/75 dark:text-dulia-parchment/75">
              Loading…
            </CardBody>
          </Card>
        ) : sorted.length === 0 ? (
          <Card className="rounded-[24px]">
            <CardBody className="text-dulia-navy/75 dark:text-dulia-parchment/75">
              No matches.
            </CardBody>
          </Card>
        ) : (
          <>
            <Card className="overflow-hidden rounded-[28px]">
              <div className="divide-y divide-dulia-line dark:divide-dulia-lineStrong/20">
                {paged.map((s) => {
                  const meta = [s.isPope ? "Pope" : null, s.stage || "—"].filter(Boolean);
                  const hero = heroFromSaint(s);
                  const hasCountry = !!(s?.countryLower || s?.country);
                  const favoritesCount = getFavoritesCount(s);
                  const viewsCount = getViewsCount(s);

                  const goToProfile = () => navigate(`/profile/${s.id}`);

                  return (
                    <div
                      key={s.id}
                      className="group p-4 transition-colors duration-150 hover:cursor-pointer hover:bg-dulia-parchment/80 dark:hover:bg-dulia-night"
                      role="link"
                      tabIndex={0}
                      onClick={goToProfile}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          goToProfile();
                        }
                      }}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex min-w-0 flex-1 items-center gap-4">
                          <div
                            className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-dulia-line bg-dulia-parchment shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-night"
                            aria-label={`View ${s.name || s.id}`}
                          >
                            {hero ? (
                              <img
                                src={hero}
                                alt=""
                                className="h-full w-full object-cover object-top"
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ) : null}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="truncate text-base font-semibold text-dulia-ink dark:text-dulia-parchment">
                              {s.name || s.id}
                            </div>

                            <div className="mt-1 truncate text-sm text-dulia-navy/75 dark:text-dulia-parchment/70">
                              <span>{meta.join(" • ")}</span>
                              {hasCountry ? (
                                <span className="inline-flex items-center gap-1">
                                  <span className="ml-1">•</span>
                                  <CountryFlag
                                    saint={s}
                                    imgClassName="h-4 w-6 rounded-sm border border-black/10 object-cover"
                                  />
                                </span>
                              ) : null}
                              {feastSummary(s)}
                            </div>

                            {Array.isArray(s?.patronage) && s.patronage.length > 0 ? (
                              <div className="mt-1 truncate text-sm text-dulia-navy/70 dark:text-dulia-parchment/65">
                                {patronagePreview(s, 3)}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <div className="hidden items-center gap-2 sm:flex">
                            <MetricPill
                              tone="gold"
                              icon={<FaHeart className="text-[12px]" aria-hidden="true" />}
                              value={favoritesCount}
                            />
                            {/* <MetricPill
                              icon={<FaEye className="text-[12px]" aria-hidden="true" />}
                              value={viewsCount}
                            /> */}
                          </div>

                          {showAdminActions && isAdmin ? (
                            <Link
                              className="inline-flex items-center justify-center rounded-xl border border-dulia-line bg-dulia-panel p-2 text-sm text-dulia-navy transition hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment dark:hover:bg-dulia-nightSoft"
                              to={`/admin/edit/${s.id}`}
                              title="Edit"
                              aria-label="Edit"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FaPencilAlt aria-hidden="true" />
                            </Link>
                          ) : null}

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              shareSaint(s);
                            }}
                            className="inline-flex items-center justify-center rounded-xl border border-dulia-line bg-dulia-panel p-2 text-sm text-dulia-navy transition hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment dark:hover:bg-dulia-nightSoft"
                            title="Share"
                            aria-label="Share"
                          >
                            <FaShareAlt aria-hidden="true" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center gap-2 sm:hidden">
                        <MetricPill
                          tone="gold"
                          icon={<FaHeart className="text-[12px]" aria-hidden="true" />}
                          value={favoritesCount}
                        />
                        <MetricPill
                          icon={<FaEye className="text-[12px]" aria-hidden="true" />}
                          value={viewsCount}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {totalPages > 1 ? (
              <div className="flex justify-center">
                <Card className="w-max rounded-2xl">
                  <CardBody className="flex flex-wrap items-center justify-center gap-1">
                    <Button
                      type="button"
                      onClick={() => goToPage(page - 1)}
                      disabled={page <= 1}
                      aria-label="Previous page"
                      title="Previous page"
                    >
                      <span aria-hidden="true">←</span>
                    </Button>

                    {pageSeries(totalPages, page)
                      .filter((p) => p !== "…")
                      .map((p) => (
                        <PaginationButton
                          key={p}
                          active={p === page}
                          onClick={() => goToPage(p)}
                          aria-current={p === page ? "page" : undefined}
                        >
                          {p}
                        </PaginationButton>
                      ))}

                    <Button
                      type="button"
                      onClick={() => goToPage(page + 1)}
                      disabled={page >= totalPages}
                      aria-label="Next page"
                      title="Next page"
                    >
                      <span aria-hidden="true">→</span>
                    </Button>
                  </CardBody>
                </Card>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}